import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { translate } from 'translations/i18n';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import { ButtonCondensed } from '@companycam/slab-web';
import Steven from 'helpers/steven';
import toast from 'helpers/toast';
import {
  useUpdateProjectTaskPosition,
  getSourceFromTaskAssets,
  PROJECT_TASK_SOURCE,
} from 'data/v3';
import ProjectTask from './ProjectTask';
import { TOOLTIP_MOUNT_NODE_ID } from './constants';
import DraggableWrapper from 'components/projects/projectTasks/DraggableWrapper';
import GalleryContainer from 'components/assets/GalleryContainer';
import Spinner from 'components/shared/DesignSystem/Spinner';
import styled from 'styled-components';

const TasksContainer = styled.div`
  width: calc(100% + 24px);
  margin-top: 4px;
  margin-left: -12px;

  &:empty {
    display: none;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-left: var(--cc_size_spacing_s);
`;

const CompletedTasksContainer = styled.div`
  width: calc(100% - 8px);
  padding-top: var(--cc_size_spacing_m);
  margin-top: var(--cc_size_spacing_m);
  border-top: 1px solid var(--cc_color_border_default);
`;

const buttonStyle = {
  marginTop: 'var(--cc_size_spacing_s)',
  marginLeft: -'var(--cc_size_spacing_s)',
};

const ProjectTasksList = ({
  loading,
  incompleteTasks,
  completedTasks,
  createProjectTask,
  addNewTask,
  addNewTaskAtEnd,
  removeNewTask,
  projectInfo,
  isGalleryView,
  assetDbId,
}) => {
  const [showCompleted, setShowCompleted] = useState(false);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [galleryTaskAssets, setGalleryTaskAssets] = useState([]);
  const [initialGalleryAsset, setInitialGalleryAsset] = useState(null);

  const updateProjectTaskPosition = useUpdateProjectTaskPosition({
    projectDbId: projectInfo.databaseId,
    onSuccess: ({ updatedTask }) => {
      if (updatedTask) {
        const source = getSourceFromTaskAssets({
          task: updatedTask,
        });

        Steven.entityCrud({
          entity_id: updatedTask.id.toString(),
          entity_type: 'project_task',
          action: 'updated',
          updated_action: 'position_updated',
          source: isGalleryView ? source : PROJECT_TASK_SOURCE.PROJECT,
        });
      }
    },
    onError: () => {
      toast.error(translate('projectTasks.updateTaskPositionErrorMsg'));
    },
  });

  const handleTaskAssetClick = ({ initialAsset, taskAssets }) => {
    setGalleryTaskAssets(taskAssets);
    setInitialGalleryAsset(initialAsset);
    setIsGalleryOpen(true);
  };

  const onDragEnd = ({ destination, source }) => {
    if (!destination) {
      return;
    }

    // If the item is dropped in the same place, do nothing
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    const updatedTask = incompleteTasks[source.index];
    const newPosition = incompleteTasks[destination.index].position;

    updateProjectTaskPosition.mutate({
      task: {
        ...updatedTask,
        position: newPosition,
      },
    });
  };

  if (loading) {
    return <Spinner size={15} label={translate('common.loading')} />;
  }

  return (
    <TasksContainer isGalleryView={isGalleryView} id={TOOLTIP_MOUNT_NODE_ID}>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable
          droppableId={
            projectInfo.databaseId?.toString() || assetDbId.toString()
          }
        >
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {incompleteTasks.map((task, index) => {
                return (
                  <DraggableWrapper
                    taskId={task.id}
                    index={index}
                    disabled={isGalleryView}
                  >
                    <ProjectTask
                      key={task.id}
                      task={task}
                      index={index}
                      onTaskAssetClick={handleTaskAssetClick}
                      createProjectTask={createProjectTask}
                      addNewTask={addNewTask}
                      removeNewTask={removeNewTask}
                      projectInfo={projectInfo}
                      isGalleryView={isGalleryView}
                      assetDbId={assetDbId}
                    />
                  </DraggableWrapper>
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      <ButtonContainer>
        <ButtonCondensed
          data-testid={
            isGalleryView
              ? 'asset__new-task-button'
              : 'project__new-task-button'
          }
          id="create-button"
          color="primary"
          icon={{
            name: 'plus-circle-outline',
            position: 'before',
          }}
          onClick={addNewTaskAtEnd}
          style={{ marginTop: 8, marginLeft: -2 }}
        >
          {translate('projectTasks.newTaskButton')}
        </ButtonCondensed>

        {completedTasks.length ? (
          <CompletedTasksContainer>
            <ButtonCondensed
              color="subtle"
              icon={{
                name: showCompleted ? 'chevron-down' : 'chevron-right',
                position: 'before',
              }}
              onClick={() =>
                setShowCompleted((prevShowCompleted) => !prevShowCompleted)
              }
              style={buttonStyle}
            >
              {showCompleted
                ? translate('projectTasks.hideCompletedButton')
                : translate('projectTasks.showCompletedButton')}
            </ButtonCondensed>
          </CompletedTasksContainer>
        ) : null}
      </ButtonContainer>

      {showCompleted
        ? completedTasks.map((task, index) => {
            return (
              <ProjectTask
                key={task.id}
                task={task}
                index={index}
                onTaskAssetClick={handleTaskAssetClick}
                createProjectTask={createProjectTask}
                addNewTask={addNewTask}
                removeNewTask={removeNewTask}
                projectInfo={projectInfo}
                isGalleryView={isGalleryView}
                assetDbId={assetDbId}
              />
            );
          })
        : null}

      {isGalleryView ? null : (
        <GalleryContainer
          initialAsset={initialGalleryAsset}
          assets={galleryTaskAssets}
          onRequestClose={() => setIsGalleryOpen(false)}
          isOpen={isGalleryOpen}
        />
      )}
    </TasksContainer>
  );
};

ProjectTasksList.defaultProps = {
  isGalleryView: false,
};

ProjectTasksList.propTypes = {
  incompleteTasks: PropTypes.array.isRequired,
  completedTasks: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  isGalleryView: PropTypes.bool,
  addNewTask: PropTypes.func,
  addNewTaskAtEnd: PropTypes.func,
  removeNewTask: PropTypes.func,
  createProjectTask: PropTypes.object,
  projectTaskListId: PropTypes.string,
  projectInfo: PropTypes.shape({
    databaseId: PropTypes.number.isRequired || PropTypes.string.isRequired,
    nodeId: PropTypes.string.isRequired || PropTypes.number.isRequired,
    formattedLine1: PropTypes.string.isRequired,
    formattedLine2: PropTypes.string,
  }).isRequired,
  assetDbId: PropTypes.string || PropTypes.number,
};

export default ProjectTasksList;
